import { Component, Suspense, type ReactNode } from 'react'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/chunkReload'
import PageLoader from '@/components/common/PageLoader'

interface Props {
  children: ReactNode
}

// Inner boundary whose only job is to catch chunk-load failures from
// React.lazy and trigger a guarded reload. Anything non-chunk is re-thrown
// to the outer AppErrorBoundary by returning hasError=false and letting
// React bubble up — so we rethrow from render to keep behavior predictable.
class ChunkReloadBoundary extends Component<Props, { bubble: Error | null }> {
  constructor(props: Props) {
    super(props)
    this.state = { bubble: null }
  }

  static getDerivedStateFromError(error: Error): { bubble: Error | null } {
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk(`LazyRoute: ${error.message}`)
      return { bubble: null }
    }
    return { bubble: error }
  }

  render(): ReactNode {
    if (this.state.bubble) {
      // Re-throw so the outer AppErrorBoundary handles non-chunk errors.
      throw this.state.bubble
    }
    return this.props.children
  }
}

export function LazyRoute({ children }: Props) {
  return (
    <ChunkReloadBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ChunkReloadBoundary>
  )
}
